import {load as initCaptcha} from 'recaptcha-v3';
import {useGlobalStore} from '../stores/global';

export default function submitForm(submitButtonId: string, action: string) {
    const store = useGlobalStore();

    const submitButton = document.getElementById(submitButtonId.replace(/^#/, '')) as HTMLButtonElement;
    if (!submitButton) {
        return;
    }

    const form = submitButton.closest('form') as HTMLFormElement;
    if (!form) {
        return;
    }

    const handleFormSubmission = async (event: Event) => {
        // Prevent multiple submits
        if (submitButton.disabled) {
            return;
        }
        submitButton.disabled = true;

        // Prevent default form submission so we can handle reCaptcha
        event.preventDefault();
        try {
            // Validate the form before proceeding
            if (!isFormValid()) {
                submitButton.disabled = false;
                return;
            }

            // Check if reCaptcha is enabled by looking for the hidden input field
            const responseTokenInput = form.querySelector("input[name='g-recaptcha-response']") as HTMLInputElement;
            if (responseTokenInput) {
                const recaptcha = await initCaptcha(store.recaptcha);
                responseTokenInput.value = await recaptcha.execute(action);
            }

            // Prevent submit loop by removing the listener.
            form.removeEventListener('submit', handleFormSubmission);
            // Submit the form for real this time.
            if (typeof form.requestSubmit === 'function') {
                form.requestSubmit();
            } else {
                form.submit();
            }
        } finally {
            submitButton.disabled = false;
        }
    };

    const isFormValid = (): boolean => {
        clearValidationErrors();
        return validateRequiredFields();
    };

    const clearValidationErrors = () => {
        const requiredFields = form.querySelectorAll('[required]');
        requiredFields.forEach(field => field.classList.remove('not-valid'));
    };

    const validateRequiredFields = (): boolean => {
        const requiredFields = form.querySelectorAll('[required]');
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        let isValid = true;
        const radioGroups: { [key: string]: boolean } = {};

        requiredFields.forEach(field => {
            const input = field as HTMLInputElement | HTMLSelectElement;
            if (input.tagName === 'INPUT') {
                if (input.type === 'checkbox') {
                    if (!input.checked) {
                        markFieldAsInvalid(input);
                        isValid = false;
                    }
                } else if (input.type === 'radio') {
                    if (!radioGroups[input.name]) {
                        radioGroups[input.name] = false;
                    }
                    if (input.checked) {
                        radioGroups[input.name] = true;
                    }
                } else if (input.type === 'email') {
                    if (!emailRegex.test(input.value)) {
                        markFieldAsInvalid(input);
                        isValid = false;
                    }
                } else if (!input.value) {
                    markFieldAsInvalid(input);
                    isValid = false;
                }
            } else if (input.tagName === 'SELECT' && !input.value) {
                markFieldAsInvalid(input);
                isValid = false;
            }
        });

        // Check if all radio groups have at least one selected input
        Object.keys(radioGroups).forEach(groupName => {
            if (!radioGroups[groupName]) {
                const group = form.querySelectorAll(`input[name="${groupName}"]`);
                group.forEach(radio => markFieldAsInvalid(radio as HTMLElement));
                isValid = false;
            }
        });

        return isValid;
    };

    const markFieldAsInvalid = (field: HTMLElement) => {
        field.classList.add('not-valid');
    };

    form.addEventListener('submit', handleFormSubmission);
}
